import React, { useContext, createContext, useState, useCallback, useMemo, PropsWithChildren } from 'react'

import { v4 as uuidv4 } from 'uuid'

import { Stack } from '@mui/material'

import Toast from '@/components/Toast'
import { TToast, TToastOptions, ToastStyle } from '@/components/Toast/types'

export const ToastContext = createContext<
  | {
      addToast: (toast: TToast) => void
    }
  | undefined
>(undefined)

export const ToastProvider = ({ children, ...props }: { children: React.ReactNode } & ToastStyle) => {
  const [toasts, setToasts] = useState<TToast[]>([])

  const addToast = useCallback((toast: TToast) => {
    setToasts((arr) => [...arr, toast])
  }, [])

  const removeToast = useCallback((key: string) => {
    setToasts((arr) => arr.filter((m) => m.key !== key))
  }, [])

  const contextValue = useMemo(() => ({ addToast }), [addToast])

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer>
        {toasts.map((toast) => (
          <Toast key={toast.key} toast={toast} onExited={() => removeToast(toast.key)} {...props} />
        ))}
      </ToastContainer>
    </ToastContext.Provider>
  )
}

const ToastContainer = ({ children }: PropsWithChildren) => {
  return (
    <Stack
      sx={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingRight: 2,
        gap: 1,
        position: 'fixed',
        zIndex: 999999,
        top: 0,
        right: 0,
        flexDirection: 'column-reverse',
        width: '100%',
      }}
    >
      {children}
    </Stack>
  )
}

export const useToast = () => {
  const context = useContext(ToastContext)

  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }

  const { addToast } = context

  const show = useCallback(
    (message: string, options: TToastOptions) => {
      addToast({ message, options, key: uuidv4() })
    },
    [addToast]
  )

  return useMemo(
    () => ({
      show,
      info(content: string, options?: Omit<TToastOptions, 'severity'>) {
        show(content, { ...options, severity: 'info' })
      },
      success(content: string, options?: Omit<TToastOptions, 'severity'>) {
        show(content, { ...options, severity: 'success' })
      },
      warning(content: string, options?: Omit<TToastOptions, 'severity'>) {
        show(content, { ...options, severity: 'warning' })
      },
      error(content: string, options?: Omit<TToastOptions, 'severity'>) {
        show(content, { ...options, severity: 'error' })
      },
    }),
    [show]
  )
}
